import React, { useEffect } from 'react';
import { Target, Eye, Heart, Users, Award, Zap, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { SectionTitle } from '../components/SectionTitle';
import { AnimatedCard } from '../components/AnimatedCard';
import { companyInfo, teamMembers, stats } from '../mock';

export const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-slide-up">
            <h1 className="text-5xl md:text-7xl font-bold tracking-wider mb-6 led-text animate-led-pulse" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
              ABOUT MAX BIEN
            </h1>
            <p className="text-xl md:text-2xl text-white" style={{ textShadow: '0 0 10px #00a8ff' }}>
              {companyInfo.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 bg-[rgba(10,37,64,0.4)] relative">
        <div className="container mx-auto px-6">
          <SectionTitle subtitle="What drives us every day">
            OUR CORE VALUES
          </SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Our Mission', desc: 'To empower businesses with innovative technology solutions that drive growth, efficiency, and digital transformation.', color: '#00a8ff' },
              { icon: Eye, title: 'Our Vision', desc: 'To become the most trusted technology partner, known for excellence, innovation, and delivering measurable business value.', color: '#4cd3ff' },
              { icon: Heart, title: 'Our Values', desc: 'Excellence, integrity, innovation, and collaboration are at the core of everything we do for our clients.', color: '#00a8ff' }
            ].map((item, index) => (
              <AnimatedCard key={index} delay={index * 150} className="group animate-float" style={{ animationDelay: `${index}s` }}>
                <div className="w-20 h-20 bg-[rgba(0,168,255,0.2)] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#00a8ff] group-hover:rotate-12 transition-all duration-500" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                  <item.icon className="text-[#00a8ff] group-hover:text-[#0a2540] transition-colors" size={40} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
                </div>
                <h3 className="text-2xl font-bold mb-4 led-text text-center" style={{ textShadow: '0 0 10px #00a8ff' }}>{item.title}</h3>
                <p className="text-white text-center" style={{ textShadow: '0 0 5px #00a8ff' }}>{item.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-gradient-to-r from-[#0a2540] via-[#1a365d] to-[#0a2540] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-on-scroll opacity-0 hover:scale-110 transition-transform duration-300" style={{ transitionDelay: `${index * 100}ms`, transform: 'translateY(30px)' }}>
                <div className="text-5xl font-bold led-text mb-2 animate-pulse-scale" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
                  {stat.value}
                </div>
                <div className="text-sm text-[#00a8ff]" style={{ textShadow: '0 0 10px #00a8ff' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#4cd3ff] rounded-full blur-3xl animate-float"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <SectionTitle subtitle="Talented professionals dedicated to your success">
            MEET OUR TEAM
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <AnimatedCard key={member.id} delay={index * 150} className="group text-center animate-float" style={{ animationDelay: `${index * 0.5}s` }}>
                <div className="relative w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#00a8ff] group-hover:border-[#4cd3ff] transition-colors duration-500" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"  width={1200} height={800} />
                  <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,168,255,0.3)] to-transparent"></div>
                </div>
                <h3 className="text-xl font-bold mb-2 led-text group-hover:text-white transition-colors" style={{ textShadow: '0 0 10px #00a8ff' }}>{member.name}</h3>
                <p className="text-[#00a8ff] mb-4" style={{ textShadow: '0 0 5px #00a8ff' }}>{member.position}</p>
                <p className="text-sm text-white" style={{ textShadow: '0 0 5px #00a8ff' }}>{member.bio}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[rgba(10,37,64,0.4)] relative">
        <div className="container mx-auto px-6">
          <SectionTitle subtitle="What sets us apart">
            WHY CHOOSE MAX BIEN?
          </SectionTitle>
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { icon: Users, title: 'Expert Team', desc: 'Our team consists of highly skilled developers, designers, and consultants with years of industry experience.' },
              { icon: Target, title: 'Client-Focused Approach', desc: 'We prioritize understanding your business goals and delivering solutions that create real value.' },
              { icon: Zap, title: 'Cutting-Edge Technology', desc: 'We stay ahead of the curve by leveraging the latest technologies and industry best practices.' },
              { icon: Award, title: 'Long-Term Partnership', desc: 'We believe in building lasting relationships and providing ongoing support beyond project completion.' }
            ].map((item, idx) => (
              <div key={idx} className="animate-on-scroll opacity-0 flex items-start space-x-6 bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl p-6 hover:shadow-[0_0_30px_#00a8ff] hover:-translate-x-4 transition-all duration-500" style={{ transitionDelay: `${idx * 150}ms`, transform: 'translateY(30px)', boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <div className="w-16 h-16 bg-[rgba(0,168,255,0.3)] rounded-xl flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 15px #00a8ff' }}>
                  <item.icon className="text-[#00a8ff]" size={32} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>{item.title}</h3>
                  <p className="text-white" style={{ textShadow: '0 0 5px #00a8ff' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .animate-on-scroll {
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .animate-on-scroll.visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
};